import Dialog from "@bmi-digital/components/dialog";
import { replaceSpaces, transformHyphens } from "@bmi-digital/components/utils";
import classnames from "classnames";
import { graphql, Link as GatsbyLink } from "gatsby";
import uniqueId from "lodash-es/uniqueId";
import React, { Suspense, useCallback, useContext, useState } from "react";
import { getPathWithCountryCode } from "../../utils/path";
import { CalculatorContext } from "../PitchedRoofCalculator";
import { useSiteContext } from "../Site";
import { VisualiserContext } from "../Visualiser";
import { sectionsMap } from "./common";
import {
  classes,
  StyledAnchorLink,
  StyledButton,
  StyledLinkDialog
} from "./styles";
import type {
  CalculatorProps,
  DialogProps,
  HubSpotProps,
  LinkProps,
  Props,
  VisualiserProps
} from "./types";

const ExternalLink = ({
  to,
  hasBrandColours,
  className,
  gtm,
  children
}: LinkProps) => {
  return (
    <StyledAnchorLink
      className={classnames(className, hasBrandColours && classes.branded)}
      href={to}
      external
      gtm={{
        id: "cta-click1",
        action: to,
        label: children,
        ...gtm
      }}
      data-testid={`external-link-${replaceSpaces(children)}`}
    >
      {transformHyphens(children)}
    </StyledAnchorLink>
  );
};

const InternalLink = ({
  to,
  hasBrandColours,
  className,
  gtm,
  children
}: LinkProps) => {
  const { countryCode } = useSiteContext();
  const url = getPathWithCountryCode(countryCode, to);

  return (
    <StyledAnchorLink
      className={classnames(className, hasBrandColours && classes.branded)}
      component={GatsbyLink}
      to={url}
      gtm={{
        id: "cta-click1",
        action: url,
        label: children,
        ...gtm
      }}
      data-testid={`internal-link-${replaceSpaces(children)}`}
    >
      {transformHyphens(children)}
    </StyledAnchorLink>
  );
};

const AssetLink = ({
  to,
  hasBrandColours,
  className,
  gtm,
  children
}: LinkProps) => {
  return (
    <StyledAnchorLink
      className={classnames(className, hasBrandColours && classes.branded)}
      href={to}
      external
      download
      gtm={{
        id: "cta-click1",
        action: to,
        label: children,
        ...gtm
      }}
      data-testid={`asset-link-${replaceSpaces(children)}`}
    >
      {transformHyphens(children)}
    </StyledAnchorLink>
  );
};

const VisualiserButton = ({
  parameters,
  onClick,
  hasBrandColours,
  className,
  children,
  gtm
}: VisualiserProps) => {
  const { open } = useContext(VisualiserContext);

  const handleOnClick = useCallback(
    (...args: unknown[]) => {
      onClick && onClick(...args);

      open?.(parameters);
    },
    [onClick, open, parameters]
  );

  return (
    <StyledButton
      className={classnames(className, hasBrandColours && classes.branded)}
      onClick={handleOnClick}
      gtm={{
        id: "cta-visualiser1",
        action: "visualiser",
        label: children,
        ...gtm
      }}
      data-testid={`visualiser-link-${replaceSpaces(children)}`}
    >
      {transformHyphens(children)}
    </StyledButton>
  );
};

const CalculatorButton = ({
  onClick,
  hasBrandColours,
  className,
  children,
  gtm
}: CalculatorProps) => {
  const { open } = useContext(CalculatorContext);

  const handleOnClick = useCallback(
    (...args: unknown[]) => {
      onClick && onClick(...args);

      open?.();
    },
    [onClick, open]
  );

  return (
    <StyledButton
      className={classnames(className, hasBrandColours && classes.branded)}
      onClick={handleOnClick}
      gtm={{
        id: "cta-calculator1",
        action: "calculator",
        label: children,
        ...gtm
      }}
      data-testid={`calculator-link-${replaceSpaces(children)}`}
    >
      {transformHyphens(children)}
    </StyledButton>
  );
};

const DialogButton = ({
  dialogContent,
  onClick,
  hasBrandColours,
  className,
  children,
  gtm
}: DialogProps) => {
  const [dialogIsOpen, setDialogIsOpen] = useState(false);

  const handleOnClick = useCallback(
    (...args: unknown[]) => {
      onClick && onClick(...args);

      setDialogIsOpen(true);
    },
    [onClick, setDialogIsOpen]
  );

  const handleDialogCloseClick = useCallback(() => {
    setDialogIsOpen(false);
  }, []);

  if (!dialogContent) {
    return;
  }

  const Component: React.ElementType = sectionsMap["form-section"];

  return (
    <>
      <StyledButton
        className={classnames(className, hasBrandColours && classes.branded)}
        onClick={handleOnClick}
        gtm={{
          id: "cta-click1",
          action: "dialog",
          label: children,
          ...gtm
        }}
        data-testid={`dialog-link-${replaceSpaces(children)}`}
      >
        {transformHyphens(children)}
      </StyledButton>
      <Dialog
        open={dialogIsOpen}
        onCloseClick={handleDialogCloseClick}
        data-testid={`dialog-container-${replaceSpaces(children)}`}
      >
        <StyledLinkDialog>
          <Suspense fallback={<div>loading...</div>}>
            <Component
              data={dialogContent}
              id={`dialog-section-${uniqueId()}`}
              isDialog
            />
          </Suspense>
        </StyledLinkDialog>
      </Dialog>
    </>
  );
};

const HubSpotLink = ({
  hubSpotCtaId,
  hasBrandColours,
  className,
  gtm,
  children
}: HubSpotProps) => {
  const to = `${process.env.GATSBY_HUBSPOT_CTA_URL}${process.env.GATSBY_HUBSPOT_ID}/${hubSpotCtaId}`;
  return (
    <StyledAnchorLink
      className={classnames(className, hasBrandColours && classes.branded)}
      href={to}
      gtm={{
        id: "cta-click1",
        action: to,
        label: children,
        ...gtm
      }}
      data-testid={`hubspot-link-${replaceSpaces(children)}`}
    >
      {transformHyphens(children)}
    </StyledAnchorLink>
  );
};

const Link = (props: Props) => {
  switch (props.data.blockType) {
    case "external-link-block":
    case "rich-text-external-link-block":
      return (
        <ExternalLink
          to={props.data.url}
          hasBrandColours={props.hasBrandColours}
          className={props.className}
          gtm={props.gtm}
        >
          {props.children}
        </ExternalLink>
      );
    case "internal-link-block":
    case "rich-text-internal-link-block":
      return (
        <InternalLink
          to={props.data.linkedPage.value.path}
          hasBrandColours={props.hasBrandColours}
          className={props.className}
          gtm={props.gtm}
        >
          {props.children}
        </InternalLink>
      );
    case "asset-link-block":
      return (
        <AssetLink
          to={props.data.asset.url}
          hasBrandColours={props.hasBrandColours}
          className={props.className}
          gtm={props.gtm}
        >
          {props.children}
        </AssetLink>
      );
    case "visualiser-link-block":
      return (
        <VisualiserButton
          onClick={props.onClick}
          parameters={props.data.parameters}
          hasBrandColours={props.hasBrandColours}
          className={props.className}
          gtm={props.gtm}
        >
          {props.children}
        </VisualiserButton>
      );
    case "calculator-link-block":
      return (
        <CalculatorButton
          onClick={props.onClick}
          hasBrandColours={props.hasBrandColours}
          className={props.className}
          gtm={props.gtm}
        >
          {props.children}
        </CalculatorButton>
      );
    case "dialog-link-block":
      return (
        <DialogButton
          onClick={props.onClick}
          dialogContent={props.data.dialogContent}
          hasBrandColours={props.hasBrandColours}
          className={props.className}
          gtm={props.gtm}
        >
          {props.children}
        </DialogButton>
      );
    case "hubspot-link-block":
      return (
        <HubSpotLink
          hubSpotCtaId={props.data.hubSpotCTAID}
          hasBrandColours={props.hasBrandColours}
          className={props.className}
          gtm={props.gtm}
        >
          {props.children}
        </HubSpotLink>
      );
  }
};

export default Link;

export const query = graphql`
  fragment InternalLinkBlockFragment on PayloadInternalLinkBlock {
    blockType
    label
    icon
    isLabelHidden
    linkedPage {
      value {
        ... on PayloadHomePage {
          path
        }
        ... on PayloadPage {
          path
        }
      }
    }
  }

  fragment RichTextInternalLinkBlockFragment on PayloadRichTextInternalLinkBlock {
    blockType
    label
    linkedPage {
      value {
        ... on PayloadHomePage {
          path
        }
        ... on PayloadPage {
          path
        }
      }
    }
  }

  fragment RichTextLinkInternalLinkBlockFragment on PayloadRichTextLinkInternalLinkBlock {
    blockType
    linkedPage {
      value {
        ... on PayloadHomePage {
          path
        }
        ... on PayloadPage {
          path
        }
      }
    }
  }

  fragment ExternalLinkBlockFragment on PayloadExternalLinkBlock {
    blockType
    label
    icon
    isLabelHidden
    url
  }

  fragment RichTextExternalLinkBlockFragment on PayloadRichTextExternalLinkBlock {
    blockType
    label
    url
  }

  fragment RichTextLinkExternalLinkBlockFragment on PayloadRichTextLinkExternalLinkBlock {
    blockType
    url
  }

  fragment HubSpotLinkBlockFragment on PayloadHubSpotLinkBlock {
    blockType
    label
    hubSpotCTAID
  }

  fragment RichTextHubSpotLinkBlockFragment on PayloadRichTextHubSpotLinkBlock {
    blockType
    label
    hubSpotCTAID
  }

  fragment RichTextLinkHubSpotLinkBlockFragment on PayloadRichTextLinkHubSpotLinkBlock {
    blockType
    hubSpotCTAID
  }

  fragment AssetLinkBlockFragment on PayloadAssetLinkBlock {
    blockType
    label
    asset {
      url
    }
  }

  fragment RichTextAssetLinkBlockFragment on PayloadRichTextAssetLinkBlock {
    blockType
    label
    asset {
      url
    }
  }

  fragment RichTextLinkAssetLinkBlockFragment on PayloadRichTextLinkAssetLinkBlock {
    blockType
    asset {
      url
    }
  }

  fragment PayloadCalculatorLinkBlockFragment on PayloadCalculatorLinkBlock {
    blockType
    label
  }

  fragment PayloadRichTextCalculatorLinkBlockFragment on PayloadRichTextCalculatorLinkBlock {
    blockType
    label
  }

  fragment PayloadDialogLinkBlockFragment on PayloadDialogLinkBlock {
    blockType
    label
    dialogContent {
      ...FormDataFragmentNonRecursive
    }
  }

  fragment PayloadRichTextDialogLinkBlockFragment on PayloadRichTextDialogLinkBlock {
    blockType
    label
    dialogContent {
      ...FormDataFragmentNonRecursive
    }
  }

  # Used by the rich text non recursive fragments, so if a form section is used in a rich text as part of a dialog
  # block, it can't have another dialog link inside
  fragment PayloadRichTextDialogLinkBlockFragmentNonRecursive on PayloadRichTextDialogLinkBlock {
    blockType
    label
    dialogContent {
      ...FormDataFragmentNonRecursive
    }
  }

  fragment PayloadVisualiserLinkBlockFragment on PayloadVisualiserLinkBlock {
    blockType
    label
    parameters {
      tileId
      colourId
      sidingId
      viewMode
    }
  }

  fragment PayloadRichTextVisualiserLinkBlockFragment on PayloadRichTextVisualiserLinkBlock {
    blockType
    label
    parameters {
      tileId
      colourId
      sidingId
      viewMode
    }
  }
`;
